import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  PageShell,
  Pagination,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import type { ClaimSummary, ClaimType } from '@/lib/api-admin/types';
import { labelOf, CLAIM_TYPE_LABELS, PAYMENT_METHOD_LABELS } from '@/lib/enumLabels';
import { useClaimsQuery } from '../hooks/useClaimsQuery';
import { ordererNameOf } from '@/features/orders/lib/ordererName';
import { useClaimQuery } from '../hooks/useClaimQuery';
import { ClaimRefundPaymentLinks } from '../components/ClaimRefundPaymentLinks';

const PAGE_SIZE = 20;

const formatMoney = (m: { amount: number; currencyCode: string } | null | undefined): string =>
  m ? `${m.amount.toLocaleString()} ${m.currencyCode}` : '-';

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** BE order #108 — 결제수단·이관 주문 여부(배포 전이면 없음). */
type RefundRow = ClaimSummary & { buyerName?: string | null; ordererName?: string | null; paymentMethod?: string | null; legacyOrder?: boolean | null };

/**
 * 환불 방법 힌트 — 고도몰 이관 주문은 결제가 payment-service 에 없어 수기 환불(BE 회신), 무통장입금은 계좌이체 후 수기 기록,
 * 가상계좌(Toss)는 PG 환불(환불 계좌 입력) 또는 계좌이체 후 수기 기록, 그 밖은 PG 취소.
 */
const refundKind = (r: RefundRow): string | null => {
  if (r.legacyOrder) return '이관 · 수기 환불';
  if (!r.paymentMethod) return null;
  if (r.paymentMethod === 'BANK_TRANSFER') return '수기 환불';
  if (r.paymentMethod === 'VIRTUAL_ACCOUNT') return 'PG 환불(계좌) · 수기';
  return 'PG 취소';
};

/** 줄의 [환불 처리] — 클레임 상세를 읽어 클레임 상세와 같은 환불 영역(PG 취소·수기 환불·고객 계좌)을 띄운다. */
const RefundDialog = ({ claimId, onClose }: { claimId: string; onClose: () => void }) => {
  const query = useClaimQuery(claimId);
  const claim = query.data;
  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-lg" data-testid="refund-list-dialog">
        <DialogHeader>
          <DialogTitle>환불 처리</DialogTitle>
          <DialogDescription>
            {claim ? `주문 ${claim.orderNo ?? ''} · 예상 환불액 ${formatMoney(claim.expectedRefundAmount as never)}` : '클레임을 불러오는 중입니다.'}
          </DialogDescription>
        </DialogHeader>
        {query.isPending ? (
          <Skeleton className="h-[120px] w-full" />
        ) : claim ? (
          <>
            <ClaimRefundPaymentLinks claim={claim as unknown as Parameters<typeof ClaimRefundPaymentLinks>[0]['claim']} />
            <Link to={`/claims/${claimId}`} className="text-xs underline text-[color:var(--content-muted)]">
              클레임 상세 보기
            </Link>
          </>
        ) : (
          <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
            클레임을 불러오지 못했습니다.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * QA-558 환불 리스트 — 승인돼 환불을 기다리는(환불접수, REFUND_PENDING) 클레임만 모은다(고도몰 [환불 리스트]).
 * 줄마다 [환불 처리]: 카드·간편결제는 PG 취소, 가상계좌·무통장·이관 결제는 수기 환불 기록. 환불이 끝나면 BE 이벤트로
 * 클레임이 환불 완료가 되어 목록에서 빠진다. 클레임 화면·결제 화면을 오가지 않아도 된다.
 */
export const RefundListPage = () => {
  const [page, setPage] = useState(1);
  const [claimType, setClaimType] = useState<ClaimType | ''>('');
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [openClaimId, setOpenClaimId] = useState<string | null>(null);

  const query = useClaimsQuery({
    page,
    size: PAGE_SIZE,
    sort: 'requestedAt,asc',
    claimStatus: 'REFUND_PENDING',
    claimType: claimType || null,
    ...(q ? { q } : {}),
  });
  const items = (query.data?.items ?? []) as ReadonlyArray<RefundRow>;
  const total = query.data?.meta.pagination?.totalItems ?? 0;

  return (
    <PageShell title="환불 리스트">
      <p className="mb-3 text-[12px] text-[color:var(--content-muted)]">
        클레임 승인 후 환불을 기다리는(환불접수) 건입니다. [환불 처리]에서 카드·간편결제는 PG 취소, 가상계좌·무통장은 계좌이체 후 수기
        환불 기록을 합니다. 환불이 끝나면 클레임이 환불 완료로 바뀌어 목록에서 빠집니다.
      </p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          className="w-32"
          value={claimType}
          onChange={(e) => {
            setClaimType(e.target.value as ClaimType | '');
            setPage(1);
          }}
          aria-label="클레임 유형"
          data-testid="refund-list-type"
        >
          <option value="">전체 유형</option>
          <option value="CANCEL">{labelOf(CLAIM_TYPE_LABELS, 'CANCEL')}</option>
          <option value="RETURN">{labelOf(CLAIM_TYPE_LABELS, 'RETURN')}</option>
          <option value="EXCHANGE">{labelOf(CLAIM_TYPE_LABELS, 'EXCHANGE')}</option>
        </Select>
        <Input
          className="w-64"
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setQ(input.trim());
              setPage(1);
            }
          }}
          placeholder="상품명 / 주문번호 / 주문자명"
          aria-label="검색어"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setQ(input.trim());
            setPage(1);
          }}
        >
          검색
        </Button>
        <span className="ml-auto text-sm text-[color:var(--content-default)]" data-testid="refund-list-total">
          환불 대기 {total.toLocaleString()}건
        </span>
      </div>

      {query.isPending ? (
        <Skeleton className="h-[200px] w-full" />
      ) : query.isError ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          환불 리스트를 불러오지 못했습니다.
        </p>
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-[color:var(--content-muted)]" data-testid="refund-list-empty">
          환불을 기다리는 건이 없습니다.
        </p>
      ) : (
        <Table data-testid="refund-list-table">
          <TableHeader style={{ background: 'var(--surface-1)' }}>
            <TableRow>
              <TableHead scope="col">주문번호</TableHead>
              <TableHead scope="col">주문자명</TableHead>
              <TableHead scope="col">유형</TableHead>
              <TableHead scope="col">상품</TableHead>
              <TableHead scope="col">결제수단</TableHead>
              <TableHead scope="col">예상 환불액</TableHead>
              <TableHead scope="col">신청일시</TableHead>
              <TableHead scope="col">처리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.claimId} data-testid="refund-list-row">
                <TableCell>
                  <Link to={`/claims/${c.claimId}`} className="underline">
                    {c.orderNo ?? '-'}
                  </Link>
                </TableCell>
                <TableCell>{ordererNameOf(c) ?? '-'}</TableCell>
                <TableCell>{labelOf(CLAIM_TYPE_LABELS, c.claimType)}</TableCell>
                <TableCell>
                  <span className="block max-w-[260px] overflow-hidden text-ellipsis whitespace-nowrap" title={c.representativeProductName ?? ''}>
                    {c.representativeProductName ?? '-'}
                    {(c.additionalItemCount ?? 0) > 0 ? ` 외 ${c.additionalItemCount}건` : ''}
                  </span>
                </TableCell>
                <TableCell data-testid="refund-list-method">
                  {c.paymentMethod ? labelOf(PAYMENT_METHOD_LABELS, c.paymentMethod) : '-'}
                  {refundKind(c) ? <div className="text-[11px] text-[color:var(--content-muted)]">{refundKind(c)}</div> : null}
                </TableCell>
                <TableCell className="tabular-nums">{formatMoney(c.expectedRefundAmount)}</TableCell>
                <TableCell className="tabular-nums">{formatDateTime(c.requestedAt)}</TableCell>
                <TableCell>
                  <Button size="sm" variant="cta" onClick={() => setOpenClaimId(c.claimId)} data-testid="refund-list-process">
                    환불 처리
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Pagination total={total} currentPage={page} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4" />

      {openClaimId ? <RefundDialog claimId={openClaimId} onClose={() => setOpenClaimId(null)} /> : null}
    </PageShell>
  );
};
